// Shared light/dark theme hook + tiny toggle component. Every SPA in
// the suite imports the same `useTheme` so the user gets identical
// behaviour everywhere — toggle in one app and (optionally) the
// preference can sync via localStorage if the apps share an origin or
// a backend setting in the future.
import { useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "athavita-suite-theme";

function readInitialTheme(): Theme {
    if (typeof window === "undefined") return "light";
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark") return stored;
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * One hook to rule them all — every SPA's shell calls this and the
 * `.dark` class is toggled on `<html>`. Persists per-browser, not
 * per-app, so flipping in Mail also flips Docs/Sheet/Drive/etc. on
 * the next render in those tabs.
 */
export function useSuiteTheme() {
    const [theme, setTheme] = useState<Theme>(readInitialTheme);

    useEffect(() => {
        const root = document.documentElement;
        root.classList.toggle("dark", theme === "dark");
        window.localStorage.setItem(STORAGE_KEY, theme);
    }, [theme]);

    // React to system theme changes the user makes elsewhere.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = () => {
            // Only follow the system if the user hasn't expressed a preference yet.
            if (window.localStorage.getItem(STORAGE_KEY)) return;
            setTheme(mq.matches ? "dark" : "light");
        };
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    return {
        theme,
        setTheme,
        toggle: () => setTheme((t) => (t === "light" ? "dark" : "light"))
    };
}
